import { Component, OnInit, ViewChildren, QueryList, AfterViewInit, ChangeDetectionStrategy, OnDestroy, ElementRef } from '@angular/core';
import { WidgetComponent } from 'src/app/shared/components/widget/widget.component';
import { Subscription, Observable, Subject } from 'rxjs';
import { Select, Store, Actions, ofActionDispatched } from '@ngxs/store';
import { EngineState } from 'src/app/engine/state/engine.state';
import { StoryState } from 'src/app/story/state/story.state';
import { SelectLabel, RemoveAllChapterLabels, ConvertLabels } from 'src/app/story/state/labels/story.labels.actions';
import { StoryModel } from 'src/app/story/models/story.model';
import { StoryLabelsState } from 'src/app/story/state/labels/story.labels.state';
import { LabelModel } from 'src/app/story/models/labels/label.model';
import { ToolEnum } from 'src/app/engine/models/tool.enum';
import { NextView, PreviousView } from '../../state/edit.actions';
import { FormBuilder, FormGroup } from '@angular/forms';
import { LabelWidgetOpened } from './label-widget/label-widget.component';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { filter, takeUntil } from 'rxjs/operators';
import { ChapterModel } from 'src/app/story/models/chapter.model';
import { MatCheckboxChange } from '@angular/material';
import { EngineCameraState } from 'src/app/engine/state/camera/engine.camera.state';
import { LockCamera } from 'src/app/engine/state/camera/engine.camera.actions';
import { LabelRecipe } from 'src/app/story/models/labels/label-recipe.enum';

@Component({
  selector: 'app-edit-label-context',
  templateUrl: './edit-label-context.component.html',
  styleUrls: ['./edit-label-context.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditLabelContextComponent implements OnInit, AfterViewInit, OnDestroy {
  activeToolEnum = ToolEnum;
  labelRecipe = LabelRecipe;

  @ViewChildren(WidgetComponent) labelWidgets: QueryList<WidgetComponent>;

  @Select(StoryState.getStory)
  story$: Observable<StoryModel>;

  @Select(StoryChaptersState.getSelectedChapter)
  selectedChapter$: Observable<ChapterModel>;

  @Select(StoryLabelsState.getChapterLabels)
  labels$: Observable<LabelModel[]>;

  @Select(StoryLabelsState.getSelectedLabel)
  selectedLabel$: Observable<LabelModel>;

  // Engine
  @Select(EngineState.getChapter)
  chapter$: Observable<string>;

  @Select(EngineState.getActiveTool)
  activeTool$: Observable<ToolEnum>;

  // Engine Camera
  @Select(EngineCameraState.getLockCamera)
  lockCamera$: Observable<boolean>;
  
  interactive = false;
  widgetSubscriptions: Subscription[] = [];
  labelForm: FormGroup;

  private ngUnsubscribe = new Subject();

  constructor(
    private store: Store,
    private formBuilder: FormBuilder,
    private actions: Actions
  ) {
    this.labelForm = this.formBuilder.group({
      title: [''],
      description: ['']
    });
  }
  
  ngOnInit() {
    this.bindApplicationEvents();

    // scroll to selected label
    this.selectedLabel$
      .pipe(
        filter((selectedLabel) => !!selectedLabel),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((selectedLabel) => {
        setTimeout(() => this.scrollLabelIntoView(selectedLabel));
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
  
  ngAfterViewInit() {
    this.subscribeToOpen();

    // Label widgets are dynamically updated, so make sure to update our event subscriptions
    // First unsubscribe, in order to prevent duplicate subscriptions.
    this.labelWidgets.changes.subscribe(() => {
      this.unsubscribeToOpen();
      this.subscribeToOpen();
    });
    
  }

  bindApplicationEvents() {
    this.selectedChapter$.pipe(
      filter((chapter) => chapter != null),
      takeUntil(this.ngUnsubscribe)
    ).subscribe((chapter) => {
      this.interactive = chapter.readyState.model;
    });
  }
  
  unsubscribeToOpen() {
    this.widgetSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.widgetSubscriptions = [];
  }

  subscribeToOpen() {
    this.labelWidgets.forEach((widget) => {
      const subscription = widget.opened
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((open) => {
          // Listen for widget opening, close other widgets. Accordion style.
          this.openWidget(widget, open);
        });
        this.widgetSubscriptions.push(subscription);
    });
  }

  handleOpenWidgets(open: boolean) {
    this.toggleWidgets(open);
  }

  handleOpened(opened: LabelWidgetOpened) {
    this.store.dispatch(new SelectLabel(opened.label));
  }
  
  handleRemoveAll() {
    this.store.dispatch(new RemoveAllChapterLabels(this.store.selectSnapshot(StoryChaptersState.getSelectedChapter)));
  }

  handleNext() {
    this.store.dispatch(new NextView());
  }

  handlePrevious() {
    this.store.dispatch(new PreviousView());
  }

  handleCheckedChange(change: MatCheckboxChange) {
    this.store.dispatch(new LockCamera(change.checked));
  }

  handleConvertLabels(type: LabelRecipe, scope: 'story' | 'chapter') {
    this.convertLabels(type, scope);
  }
  
  toggleWidgets(open: boolean) {
    this.labelWidgets.forEach((widget) => {
      widget.open = open;
    });
  }
  
  openWidget(widget: WidgetComponent, open: boolean) {
    this.toggleWidgets(false);
    widget.open = open;
  }

  trackLabel(label: LabelModel) { 
    return label.id; 
  }

  convertLabels(type: LabelRecipe, scope: 'story' | 'chapter') {
    const labels = (scope === 'story') ?
      this.store.selectSnapshot(StoryLabelsState.getLabels) :
      this.store.selectSnapshot(StoryLabelsState.getChapterLabels);

    this.store.dispatch(new ConvertLabels(labels, type));
  }

  scrollLabelIntoView(label: LabelModel) {
    const target = <HTMLDivElement>document.querySelector(`[data-id="${label.id}"]`);
    const isSmoothScrollSupported = 'scrollBehavior' in document.documentElement.style;
    if (isSmoothScrollSupported && target) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }

}
